import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SPORTS } from "@/lib/content";
import { LayoutDashboard, RefreshCw, Users, CalendarDays, Newspaper, Mail, Plus, Trash2 } from "lucide-react";
import { EbookUploadPanel, MemberPostsPanel, GameLinesPanel, PicksPanel } from "@/components/AdminPanels";
function Stat({ icon: Icon, label, value }) {
  return (
    <div className="panel p-5">
      <Icon className="w-6 h-6 text-smasher-gold mb-2" />
      <div className="font-heading font-black text-3xl text-white">{value}</div>
      <div className="text-xs text-slate-400 tracking-wide">{label}</div>
    </div>
  );
}

const inputCls = "w-full bg-black/50 border border-smasher-gold/25 rounded-xl px-4 py-2.5 text-white outline-none focus:border-smasher-gold text-sm";

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [news, setNews] = useState([]);
  const [form, setForm] = useState({ sport: "football", title: "", summary: "", category: "News" });
  const [refreshing, setRefreshing] = useState(false);
  const [sched, setSched] = useState([]);
  const [sForm, setSForm] = useState({ league: "NFL", week: 1, away: "", home: "", date: "", time: "1:00 PM", venue: "", away_score: "", home_score: "", ranked: false });
  const [bulk, setBulk] = useState("");

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/leads").then((r) => setLeads(r.data || [])).catch(() => {});
    api.get("/news", { params: { limit: 100 } }).then((r) => setNews(r.data || [])).catch(() => {});
    api.get("/admin/schedule").then((r) => setSched(r.data || [])).catch(() => {});
  };
  useEffect(load, []);

  const addGame = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...sForm, week: Number(sForm.week),
        away_score: sForm.away_score === "" ? null : Number(sForm.away_score),
        home_score: sForm.home_score === "" ? null : Number(sForm.home_score) };
      await api.post("/admin/schedule", payload);
      toast.success("Game added");
      setSForm({ ...sForm, away: "", home: "", venue: "", away_score: "", home_score: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const addBulk = async (e) => {
    e.preventDefault();
    // CSV lines: league,week,away,home,date(YYYY-MM-DD),time,venue,away_score,home_score,ranked
    const games = bulk.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
      const [league, week, away, home, date, time, venue, as, hs, ranked] = l.split(",").map((x) => (x || "").trim());
      return { league: league || "NFL", week: Number(week) || 1, away, home, date, time: time || null, venue: venue || null,
        away_score: as ? Number(as) : null, home_score: hs ? Number(hs) : null, ranked: (ranked || "").toLowerCase() === "true" };
    }).filter((g) => g.away && g.home && g.date);
    if (!games.length) return toast.error("No valid rows. Format: league,week,away,home,date,time,venue,away_score,home_score,ranked");
    try {
      const { data } = await api.post("/admin/schedule/bulk", { games });
      toast.success(`${data.added} games added`);
      setBulk("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const delGame = async (id) => { await api.delete(`/admin/schedule/${id}`).catch(() => {}); load(); };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/admin/refresh");
      toast.success("Sports data refreshed", { description: `Last updated ${new Date(data.meta.last_updated).toLocaleString()}` });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setRefreshing(false);
    }
  };

  const addNews = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/news", form);
      toast.success("News published");
      setForm({ ...form, title: "", summary: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const delNews = async (id) => {
    await api.delete(`/admin/news/${id}`).catch(() => {});
    load();
  };

  return (
    <div className="max-w-[1300px] mx-auto px-4 sm:px-6 py-12 space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading font-black uppercase text-3xl sm:text-4xl text-gradient-gold flex items-center gap-3"><LayoutDashboard className="w-8 h-8 text-smasher-gold" /> Admin Dashboard</h1>
          <p className="text-slate-400 text-sm mt-1">Signed in as {user?.email}</p>
        </div>
        <button onClick={refresh} disabled={refreshing} className="btn-gold px-5 py-2.5 rounded-xl font-bold flex items-center gap-2 disabled:opacity-60" data-testid="admin-refresh">
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> REFRESH SPORTS DATA
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Stat icon={Users} label="Users" value={stats.users} />
          <Stat icon={CalendarDays} label="Events" value={stats.events} />
          <Stat icon={Newspaper} label="News Items" value={stats.news} />
          <Stat icon={Mail} label="Leads" value={stats.leads} />
        </div>
      )}
      {stats?.meta?.last_updated && (
        <div className="text-xs font-mono-num text-slate-500" data-testid="admin-last-updated">LAST UPDATED: {new Date(stats.meta.last_updated).toLocaleString()} · source: {stats.meta.source}</div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <EbookUploadPanel />
        <MemberPostsPanel />
      </div>
      <PicksPanel />
      <GameLinesPanel />

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Add news */}
        <div className="panel p-6">
          <h2 className="font-heading font-bold text-xl text-white mb-4 flex items-center gap-2"><Plus className="w-5 h-5 text-smasher-gold" /> Publish News</h2>
          <form onSubmit={addNews} className="space-y-3" data-testid="admin-news-form">
            <select value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value })} className={inputCls}>
              {SPORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <input required placeholder="Headline" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="admin-news-title" />
            <textarea required placeholder="Summary" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} className={inputCls} rows={3} data-testid="admin-news-summary" />
            <input placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} />
            <button className="btn-gold px-5 py-2.5 rounded-xl font-bold w-full" data-testid="admin-news-submit">PUBLISH</button>
          </form>
        </div>

        {/* Leads */}
        <div className="panel p-6">
          <h2 className="font-heading font-bold text-xl text-white mb-4 flex items-center gap-2"><Mail className="w-5 h-5 text-smasher-gold" /> Recent Leads</h2>
          {leads.length ? (
            <div className="space-y-2 max-h-80 overflow-auto no-scrollbar">
              {leads.map((l, i) => (
                <div key={i} className="flex items-center justify-between text-sm border-b border-white/5 py-2">
                  <span className="text-slate-200">{l.email}</span>
                  <span className="text-xs text-smasher-gold">{l.interest}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-slate-500 text-sm">No leads captured yet.</p>}
        </div>
      </div>

      {/* Manage news */}
      <div className="panel p-6">
        <h2 className="font-heading font-bold text-xl text-white mb-4 flex items-center gap-2"><Newspaper className="w-5 h-5 text-smasher-gold" /> Manage News ({news.length})</h2>
        <div className="space-y-2 max-h-96 overflow-auto no-scrollbar">
          {news.map((n) => (
            <div key={n.id} className="flex items-center justify-between gap-3 border-b border-white/5 py-2">
              <div className="min-w-0">
                <div className="text-sm text-slate-200 truncate">{n.title}</div>
                <div className="text-[11px] text-slate-500">{n.sport} · {n.category}</div>
              </div>
              <button onClick={() => delNews(n.id)} className="text-red-400 hover:text-red-300 shrink-0" data-testid={`admin-del-${n.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>

      {/* Game Schedule Manager */}
      <div className="panel p-6">
        <h2 className="font-heading font-bold text-xl text-white mb-1 flex items-center gap-2"><CalendarDays className="w-5 h-5 text-smasher-gold" /> Game Schedule Manager (NFL &amp; NCAA)</h2>
        <p className="text-slate-500 text-xs mb-4">Post weekly matchups with the favorite line. For NCAA, tick "Ranked" and only add ranked Top-25 games. Enter scores to mark a game FINAL.</p>
        <form onSubmit={addGame} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2 mb-3" data-testid="admin-sched-form">
          <select value={sForm.league} onChange={(e) => setSForm({ ...sForm, league: e.target.value })} className={inputCls} data-testid="admin-sched-league">
            <option value="NFL">NFL</option><option value="NCAA">NCAA</option>
          </select>
          <input type="number" min="1" placeholder="Week" value={sForm.week} onChange={(e) => setSForm({ ...sForm, week: e.target.value })} className={inputCls} data-testid="admin-sched-week" />
          <input placeholder="Away team" value={sForm.away} onChange={(e) => setSForm({ ...sForm, away: e.target.value })} className={inputCls} data-testid="admin-sched-away" />
          <input placeholder="Home team" value={sForm.home} onChange={(e) => setSForm({ ...sForm, home: e.target.value })} className={inputCls} data-testid="admin-sched-home" />
          <input type="date" value={sForm.date} onChange={(e) => setSForm({ ...sForm, date: e.target.value })} className={inputCls} data-testid="admin-sched-date" />
          <input placeholder="Time (1:00 PM)" value={sForm.time} onChange={(e) => setSForm({ ...sForm, time: e.target.value })} className={inputCls} />
          <input placeholder="Line (e.g. KC -3)" value={sForm.line || ""} onChange={(e) => setSForm({ ...sForm, line: e.target.value })} className={inputCls} data-testid="admin-sched-line" />
          <input placeholder="O/U (44.5)" value={sForm.over_under || ""} onChange={(e) => setSForm({ ...sForm, over_under: e.target.value })} className={inputCls} />
          <input placeholder="Venue" value={sForm.venue} onChange={(e) => setSForm({ ...sForm, venue: e.target.value })} className={`${inputCls} lg:col-span-2`} />
          <input type="number" placeholder="Away score" value={sForm.away_score} onChange={(e) => setSForm({ ...sForm, away_score: e.target.value })} className={inputCls} />
          <input type="number" placeholder="Home score" value={sForm.home_score} onChange={(e) => setSForm({ ...sForm, home_score: e.target.value })} className={inputCls} />
          <label className="flex items-center gap-2 text-sm text-slate-300 px-2"><input type="checkbox" checked={sForm.ranked} onChange={(e) => setSForm({ ...sForm, ranked: e.target.checked })} /> Ranked (NCAA)</label>
          <button className="btn-gold px-5 py-2.5 rounded-xl font-bold lg:col-span-3" data-testid="admin-sched-submit">ADD GAME</button>
        </form>
        <form onSubmit={addBulk} className="mb-4">
          <textarea value={bulk} onChange={(e) => setBulk(e.target.value)} rows={3} className={inputCls} placeholder="Bulk paste (one game per line): league,week,away,home,YYYY-MM-DD,time,venue,away_score,home_score,ranked" data-testid="admin-sched-bulk" />
          <button className="btn-ghost-gold px-5 py-2 rounded-xl font-bold mt-2" data-testid="admin-sched-bulk-submit">BULK ADD</button>
        </form>
        <div className="space-y-1.5 max-h-80 overflow-auto no-scrollbar">
          {sched.map((g) => (
            <div key={g.id} className="flex items-center justify-between gap-3 border-b border-white/5 py-1.5 text-sm">
              <span className="text-slate-300 truncate">{g.league} W{g.week}: {g.away?.abbr} @ {g.home?.abbr} {g.line ? `(${g.line})` : ""}{g.home_score != null ? ` — ${g.away_score}-${g.home_score}` : ""}</span>
              <button onClick={() => delGame(g.id)} className="text-red-400 hover:text-red-300 shrink-0" data-testid={`admin-sched-del-${g.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          {!sched.length && <p className="text-slate-500 text-sm">No games posted yet.</p>}
        </div>
      </div>
    </div>
  );
}
